/**
 * Utility class to measure the time taken by parts of the code.
 */

import {Log} from './log';

let now: () => number;
if (process.browser) {
  now = () => performance.now();
} else {
  (async () => {
    const {performance} = await import('perf_hooks');
    now = () => performance.now();
  })();
}

export class Timing {
  private startTime = now();
  private executionTimes: number[] = [];
  private isSkipped = false;
  private isEnded = false;

  /**
   * use the skip if you dont want to count the time spent in some lines of code. Example: an external fetch call
   */
  public skip() {
    if (!this.isSkipped) {
      this.executionTimes.push(now() - this.startTime);
      this.isSkipped = true;
    }
  }

  /**
   * use the resume to resume the timer after a skip
   */
  public resume() {
    if (this.isSkipped) {
      this.startTime = now();
      this.isSkipped = false;
      return;
    }
    if (process.env.NODE_ENV === 'development' && !this.isSkipped) {
      // eslint-disable-next-line no-console
      console.warn('Timing: resume is called without a skip');
    }
  }

  /**
   * end returns total time in ms since the timer was created. It excludes the time spent in skips.
   */
  public end() {
    if (!this.isEnded) {
      if (!this.isSkipped) {
        this.executionTimes.push(now() - this.startTime);
      }
      this.isEnded = true;

      return this.executionTimes.reduce((a, b) => a + b, 0);
    }

    this.executionTimes[this.executionTimes.length - 1] =
      now() - this.startTime;
    return this.executionTimes.reduce((a, b) => a + b, 0);
  }

  public measureInstance<T extends `${string}Time`>(
    name: T extends `${string} ${string}` ? never : T,
  ) {
    const start = now();

    return () => {
      const end = now();
      const time = end - start;

      Log.addAttributeToTransaction(name, time);

      return time;
    };
  }
}
